import { namespace } from '@common/config/constants';
import type { PausedSeriesEntry } from '@bt-common/schemas/bt/series-pause';
import { PAUSED_REASON_KEY, RESUME_SKIP_KEY } from '@common/types/paused-series';
import { useListPausedSeriesQuery, useResumePausedSeriesMutation } from '@common/reducers/backend-api/backend-api';
import React, { useMemo } from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import styles from './paused-series-controls.module.scss';

/** The paused-series entry for one planner row, or undefined. One shared request serves every row. */
export function usePausedSeriesEntry(rowMessageId: string): PausedSeriesEntry | undefined {
    const { data } = useListPausedSeriesQuery();
    return useMemo(() => data?.find(e => e.rowMessageId === rowMessageId), [data, rowMessageId]);
}

/** Reason line plus Resume action. Sits inside a clickable row, so it swallows its own events. */
export const PausedSeriesControls: React.FC<{ entry: PausedSeriesEntry }> = ({ entry }) => {
    const { t } = useTranslation(namespace);
    const [resume, { isLoading, isError }] = useResumePausedSeriesMutation();
    // A refused resume (series ended / cancelled / gone) drops the pause stamp, so this row unmounts on the
    // refetch: say why in a toast, which outlives it.
    const onResume = async (): Promise<void> => {
        try {
            const r = await resume({ messageId: entry.id }).unwrap();
            if (!r.resumed && r.skipped) toast.info(t(RESUME_SKIP_KEY[r.skipped])); // i18n-dynamic-key: exhaustive table in paused-series.ts
        } catch {
            // isError renders the inline retry message.
        }
    };
    const reason = t(PAUSED_REASON_KEY[entry.kind], { runs: entry.streak ?? 0 }); // i18n-dynamic-key: exhaustive table in paused-series.ts
    const stop = (e: React.SyntheticEvent) => e.stopPropagation();
    return (
        <div onClick={stop} onKeyDown={stop} className={styles.controls} data-testid="paused-series-controls">
            <span className={styles.reason}>{reason}</span>
            <button type="button" className={styles.resume} disabled={isLoading} onClick={() => { void onResume(); }}>
                {isLoading ? t('pausedSeries.resuming') : t('pausedSeries.resume')}
            </button>
            {isError && <div role="alert" className={styles.error}>{t('pausedSeries.resumeFailed')}</div>}
        </div>
    );
};
